'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { EndlessAutoScrollCarousel } from 'components/shared/EndlessAutoScrollCarousel';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import {
  RECOVERY_MARQUEE_FILENAMES,
  recoveryMarqueeSrc,
} from './recoveryPageAssets';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import styles from './RecoveryImageCarousel.module.css';

const CAROUSEL_GAP_MOBILE = '48px';
const CAROUSEL_GAP_DESKTOP = '64px';
const DESKTOP_MIN_WIDTH = 768;
const CAROUSEL_SPEED = 1;

/**
 * Per-slide sizing approximating the varied frames in the design.
 * `h` = fraction of the max track height; `aspect` = width / height.
 * Varying both produces different heights, widths and aspect ratios.
 */
const SLIDE_FRAMES: { h: number; aspect: number }[] = [
  { h: 0.78, aspect: 1.0 },
  { h: 0.96, aspect: 0.8 },
  { h: 0.84, aspect: 0.72 },
  { h: 0.9, aspect: 1.5 },
  { h: 1.0, aspect: 0.78 },
  { h: 0.72, aspect: 0.95 },
  { h: 0.92, aspect: 0.7 },
  { h: 0.86, aspect: 1.32 },
];

export const RecoveryImageCarousel: React.FC = () => {
  const t = useTranslations('performance-lab');
  const { width } = useWindowSize();
  const carouselGap =
    width >= DESKTOP_MIN_WIDTH ? CAROUSEL_GAP_DESKTOP : CAROUSEL_GAP_MOBILE;
  const ariaLabel = t('recoveryPage.imageCarousel.aria');

  const slides = useMemo(
    () =>
      RECOVERY_MARQUEE_FILENAMES.map((filename, index) => {
        const frame = SLIDE_FRAMES[index % SLIDE_FRAMES.length];
        return (
          <div className={styles.slideFrame} key={filename}>
            <div
              className={styles.slideMedia}
              style={
                {
                  '--slide-h': frame.h,
                  '--slide-aspect': frame.aspect,
                } as React.CSSProperties
              }
              aria-hidden="true"
            >
              <img
                className={styles.slideImage}
                src={getCompressedAssetUrl(`/${recoveryMarqueeSrc(filename)}`)}
                alt=""
                // Below-the-fold decorative marquee (15 slides): lazy so it
                // doesn't fire a high-priority full-res image storm that
                // competes with the hero LCP on slow mobile connections.
                loading="lazy"
              />
            </div>
          </div>
        );
      }),
    [],
  );

  return (
    <div className={styles.fullBleed} data-recovery-carousel-trigger>
      <EndlessAutoScrollCarousel
        slideWidth="auto"
        gap={carouselGap}
        speed={CAROUSEL_SPEED}
        ariaLabel={ariaLabel}
        play
        stopOnMouseEnter={false}
        stopOnFocusIn={false}
        stopOnInteraction={false}
      >
        {slides}
      </EndlessAutoScrollCarousel>
    </div>
  );
};
